import { deleteField } from 'firebase/firestore';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { appIconTypes } from 'global/enums/appIconTypes';
import { createRefPath_client } from 'global/firestoreData/appData/appRefPaths';
import { useContext, useEffect, useMemo, useReducer } from 'react';
import { Icon } from 'semantic-ui-react';
import { AppUserManagerContext } from '../cnr/contexts/AppUserManagerContext';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import { ParentContext } from '../cnr/contexts/ParentContext';
import { appUserManagerHandlers, appUserManagerInitialState, appUserManagerReducer, showAppManagerTypes, userMenuItems_edit } from '../cnr/reducers/AppUserManagerReducer';
import { uniqueKey } from '../common/keys';
import ConfirmationQuestion from '../components/alerts/ConfirmationQuestion';
import Popuper from '../components/popups/Popuper';
import UserAuth from '../components/user/UserAuth';
import UserProfile from '../components/user/UserProfile';
import { clickOriginTypes, gEnums } from '../enums/globalEnums';
import GenericPopupMenuItem from '../genericControls/GenericMenuItem';
import DirectMessage from '../messages/DirectMessage';
import { openExternal } from '../viewSettings/helpers/settingsLinks';
import FullPageWrapper from '../wrappers/FullPageWrapper';
import Wrapper, { wrapperTypes } from '../wrappers/Wrapper';
import AppUserCard from './AppUserCard';

const AppUserManager = (props) => {

  const { clickOriginType, selectedAppUser, selectedAppUserKey, handleCancel } = props

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, settings, fns, fn_cls } = parentContext ?? {}
  const { app_state, paps_state, currentSettings_state } = states
  const { database_fns } = fns
  const { mainAuth } = app_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { validation_handlers } = handlers ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appSignIn, signInEmail } = aps_global ?? {}
  const { homeSettings } = settings ?? {}
  const { global: homeSettings_global } = homeSettings ?? {}
  const { logging } = homeSettings_global ?? {}

  const appUserAccess = useAppUserAccess()
  const { accessLevel } = appUserAccess ?? {}
  const { displayName } = selectedAppUser ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { frameworkLeftType } = framework_state ?? {}

  let _fromDataViewer;

  switch (clickOriginType) {
    case clickOriginTypes.dataViewer:
    case clickOriginTypes.framework:
      _fromDataViewer = true
    default:
      if (frameworkLeftType) {
        _fromDataViewer = true
      }
    //nothing
  }

  const allowSA = appUserAccess && (accessLevel > gEnums.accessLevels.admin.value)

  // appUserManager
  const initState = appUserManagerInitialState({ fn_cls, mainAuth, paps_state, selectedAppUser, selectedAppUserKey, appSignIn, signInEmail, logging })
  const [appUserManager_state, dispatch] = useReducer(appUserManagerReducer, initState);
  const appUserManager_handlers = useMemo(
    () => appUserManagerHandlers(dispatch),
    [dispatch]
  );

  const { authRetrieved, selectedAppUserAuth, showAppManagerType } = appUserManager_state ?? {}
  const { email, _itemKey } = selectedAppUserAuth ?? {}

  useEffect(() => {
    appUserManager_handlers.handleGet_appUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [selectedAppUser]);

  const handleOpenAuth = () => openExternal.firebaseAuth()
  const handleOpenProfile = () => openExternal.firebaseProfile(paps_state.pathViews, paps_state.pathViews.clients ? email : _itemKey)
  const handleMenuItemClick = (item) => appUserManager_handlers.handleSet_selectedAppUser(item)
  const cancelSendToEmail = () => appUserManager_handlers.handleSet_selectedAppUser()
  const sendPasswordReset = () => appUserManager_handlers.handleSend_passwordResetEmail(email)
  const handleResetDisclaimerResponses = () => {
    const _refPath = createRefPath_client(pathViews, ['profiles', action.email])
    const dataToUpdate = {
      disclaimerResponses: deleteField()
    }
    // database_fns.update_doc(_refPath, dataToUpdate)
    database_fns.update_docc({
      refPath: _refPath,
      data: dataToUpdate
    })
  }

  const handleMenuItemClicker = (itemKey) => {
    switch (itemKey) {
      case userMenuItems_edit.openAuth:
        handleOpenAuth()
        break;
      case userMenuItems_edit.openProfile:
        handleOpenProfile()
        break;
      case userMenuItems_edit.resetDisclaimerResponses:
        handleResetDisclaimerResponses()
        break;
      default:
        handleMenuItemClick(itemKey)
        break;
    }
  }


  const gpmi = (item, oc, icon, iconColor) => <GenericPopupMenuItem key={uniqueKey('aum', 'gmpi', item)} item={item} oc={oc} icon={icon} iconColor={iconColor} />

  const sidebarMenuItems = useMemo(() => {
    const mis = []
    if (selectedAppUserAuth?.email) {
      mis.push(gpmi(userMenuItems_edit.resetDisclaimerResponses, handleResetDisclaimerResponses, appIconTypes.arrowRight))
      mis.push(gpmi(userMenuItems_edit.sendEmail, handleMenuItemClick, appIconTypes.arrowRight))
      mis.push(gpmi(userMenuItems_edit.sendPasswordResetEmail, handleMenuItemClick, appIconTypes.arrowRight))
      // mis.push(gpmi(userMenuItems.sendSignInLinkEmail, handleMenuItemClick))
    }

    selectedAppUserAuth?.fcmTokens && mis.push(gpmi(userMenuItems_edit.sendPushNotification, handleMenuItemClick, appIconTypes.arrowRight))
    selectedAppUserAuth?.phoneNumber && mis.push(gpmi(userMenuItems_edit.sendSms, handleMenuItemClick, appIconTypes.arrowRight))
    allowSA && mis.push(gpmi(userMenuItems_edit.openAuth, handleOpenAuth, appIconTypes.externalAlternate, 'orange'))
    allowSA && mis.push(gpmi(userMenuItems_edit.openProfile, handleOpenProfile, appIconTypes.externalAlternate, 'orange'))
    mis.unshift(gpmi(userMenuItems_edit.editProfile, handleMenuItemClick, appIconTypes.edit))
    mis.unshift(gpmi(userMenuItems_edit.editEmail, handleMenuItemClick, appIconTypes.edit))
    allowSA && mis.unshift(gpmi(userMenuItems_edit.editAuthorization, handleMenuItemClick, appIconTypes.edit))
    // selectedAppUserAuth.email && mis.push(gpmi(userMenuItems.sendSignInLinkEmail, handleMenuItemClick))
    return mis
  }, [selectedAppUserAuth])

  const appUserWrapper = () => <Wrapper
    header={_fromDataViewer && <div><Icon style={{ marginRight: '1em' }} name='arrow left' color={'blue'} onClick={(e) => { handleCancel(e) }} />{displayName}</div>}
    content={selectedAppUserAuth && <AppUserCard appUser={selectedAppUserAuth} showTreeViewer={true} handleMenuItemClicker={handleMenuItemClicker} />}
    wrapperType={wrapperTypes.padded}
    updating={!authRetrieved}
    updatingCaption={'fetching'}
    css={{ container: 'app-dark-mode' }}
  />

  const popuper = () => <Popuper
    menuItems={selectedAppUserAuth && sidebarMenuItems}
    content={appUserWrapper()}
    showPopupIcon={true}
    cn={'aum-list'}
  />

  const fullPageWrapper = () => <FullPageWrapper
    content={appUserWrapper()}
    handleCancel={handleCancel}
    topperCaption={'App User'}
    topperCaption2={email}
    isBack={true}
    wrapperCss={{ container: 'app-dark-mode', content: 'padd10' }}
  />

  const confirmation_sendPasswordReset = () => <ConfirmationQuestion
    header={'Please Confirm'}
    question={'Send password reset email ' + email + '?'}
    buttonCaption={'Send'}
    buttonIcon={'send'}
    handleNo={cancelSendToEmail}
    handleYes={sendPasswordReset}
  />

  const content = () => {
    switch (showAppManagerType) {
      // case showAppManagerTypes.emailSignInLink:
      //   return confirmation_sendSignInLink()
      case showAppManagerTypes.emailPasswordReset:
        return confirmation_sendPasswordReset()
      case showAppManagerTypes.email:
      case showAppManagerTypes.notification:
      case showAppManagerTypes.sms:
        return <DirectMessage />
      case showAppManagerTypes.auth:
        return <UserAuth inApp={true} />
      case showAppManagerTypes.profile:
        return <UserProfile clickOriginType={clickOriginType} />
      default:
        return _fromDataViewer ? popuper() : fullPageWrapper()
    }
  }

  return <AppUserManagerContext.Provider value={{ appUserManager_state, appUserManager_handlers }} >
    {content()}
  </AppUserManagerContext.Provider >

}

export default AppUserManager